import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { useAppToast } from '../components/AppToast';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useOrgMembers } from '../hooks/useOrgMembers';
import { createChronoStudy, listChronoProjects, listChronoStudies } from '../services/chronoService';
import { taktSeconds, formatChronoNumber } from '../utils/chronoStats';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Em cronometragem',
  approved: 'Aprovado',
  archived: 'Arquivado',
};

export const CronoanalisePage: React.FC = () => {
  const organizationId = useOrganizationId();
  const { user } = useAuth();
  const { members } = useOrgMembers(organizationId || undefined);
  const { showToast, ToastContainer } = useAppToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [operation, setOperation] = useState('');
  const [projectId, setProjectId] = useState('');
  const [saving, setSaving] = useState(false);

  const studiesQuery = useQuery({
    queryKey: ['chrono-studies', organizationId],
    queryFn: () => listChronoStudies(organizationId),
    enabled: Boolean(organizationId),
  });
  const projectsQuery = useQuery({
    queryKey: ['chrono-projects', organizationId],
    queryFn: () => listChronoProjects(organizationId),
    enabled: Boolean(organizationId),
  });
  const projectName = new Map((projectsQuery.data ?? []).map((project) => [project.id, project.name]));
  const memberName = new Map(members.map((member) => [member.id, member.name]));

  const create = async () => {
    if (!title.trim() || !operation.trim()) {
      showToast('warning', 'Informe o título e a operação.');
      return;
    }
    setSaving(true);
    try {
      const id = await createChronoStudy(organizationId, user?.id ?? null, {
        title,
        operationLabel: operation,
        projectId: projectId || null,
      });
      await queryClient.invalidateQueries({ queryKey: ['chrono-studies', organizationId] });
      navigate(`/cronoanalise/${id}`);
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Não foi possível criar o estudo.');
      setSaving(false);
    }
  };

  return (
    <ModuleLayout>
      <div className="flex min-h-full flex-col gap-2 p-3 text-slate-800 dark:text-slate-100">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Engenharia da Melhoria</p>
            <h1 className="text-lg font-semibold leading-tight">Cronoanálise</h1>
          </div>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="rounded bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white"
          >
            Novo estudo
          </button>
        </div>
        {open && (
          <div className="grid grid-cols-1 items-end gap-2 border border-slate-300 bg-white p-2 dark:border-slate-700 dark:bg-slate-900 md:grid-cols-[1.4fr_1.4fr_1fr_auto]">
            <label className="text-[11px]">Título
              <input value={title} onChange={(event) => setTitle(event.target.value)} className="mt-0.5 w-full border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-950" />
            </label>
            <label className="text-[11px]">Operação
              <input value={operation} onChange={(event) => setOperation(event.target.value)} className="mt-0.5 w-full border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-950" />
            </label>
            <label className="text-[11px]">Projeto
              <select value={projectId} onChange={(event) => setProjectId(event.target.value)} className="mt-0.5 w-full border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-950">
                <option value="">Nenhum</option>
                {(projectsQuery.data ?? []).map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
              </select>
            </label>
            <button type="button" disabled={saving} onClick={create} className="rounded bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900">Criar</button>
          </div>
        )}
        <div className="overflow-auto border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="bg-slate-100 text-[11px] uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="px-2 py-1.5">Estudo</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">Operação</th>
                <th className="px-2 py-1.5">Projeto</th>
                <th className="px-2 py-1.5 text-right">Takt (s)</th>
                <th className="px-2 py-1.5">Responsável</th>
                <th className="px-2 py-1.5">Abertura</th>
              </tr>
            </thead>
            <tbody>
              {(studiesQuery.data ?? []).map((study) => (
                <tr key={study.id} className="cursor-pointer border-t border-slate-200 hover:bg-blue-50 dark:border-slate-800 dark:hover:bg-slate-800" onClick={() => navigate(`/cronoanalise/${study.id}`)}>
                  <td className="px-2 py-1 font-medium">{study.title}</td>
                  <td className="px-2 py-1">{STATUS_LABEL[study.status] ?? study.status}</td>
                  <td className="px-2 py-1">{study.operationLabel}</td>
                  <td className="px-2 py-1">{study.projectId ? projectName.get(study.projectId) ?? '—' : '—'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatChronoNumber(taktSeconds(study.availableSeconds, study.demandQuantity), 2)}</td>
                  <td className="px-2 py-1">{study.createdBy ? memberName.get(study.createdBy) ?? '—' : '—'}</td>
                  <td className="px-2 py-1">{new Date(study.createdAt).toLocaleString('pt-BR')}</td>
                </tr>
              ))}
              {!studiesQuery.isLoading && (studiesQuery.data ?? []).length === 0 && (
                <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-500">Nenhum estudo nesta organização.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {studiesQuery.isError && <p className="text-xs text-red-700">{studiesQuery.error instanceof Error ? studiesQuery.error.message : 'Falha ao listar estudos.'}</p>}
      </div>
      <ToastContainer />
    </ModuleLayout>
  );
};
